/**
 * What each editable onboarding text is for, kept apart from its words so a rewording
 * never hides what the control does. Read from the screens in `packages/ui/src/v2/
 * onboarding` and the step in `packages/player`; `controls.test.ts` fails on a copy key
 * of any screen that has no entry here.
 */
export interface Control {
  /** What the text is, never its words: "Existing-account button". */
  name: string;
  /** Why it is there, or what pressing it does. */
  purpose: string;
  /** Where pressing it takes the learner. */
  goesTo?: string;
  /** When it shows, if not always. */
  shownWhen?: string;
  /** What each `{{value}}` in the text stands for. */
  values?: Record<string, string>;
}

const NEXT = 'The next screen that is turned on';

const GOALS = {
  CURRENT_JOB: 'current job',
  NEXT_ROLE: 'next role',
  CAREER_CHANGE: 'career change',
  GROW_BUSINESS: 'own business',
  LEAD_PEOPLE: 'leading people',
  UNDERSTAND_BUSINESS: 'how business works',
  MONEY_DECISIONS: 'money decisions',
  SOMETHING_ELSE: 'something else',
} as const;

/** One entry per goal, keyed `<prefix>.<GOAL>`. */
const perGoal = (
  prefix: string,
  make: (what: string) => Control,
): Record<string, Control> =>
  Object.fromEntries(
    Object.entries(GOALS).map(([id, what]) => [`${prefix}.${id}`, make(what)]),
  );

const goalAnswer = (what: string): Control => ({
  name: `Goal answer: ${what}`,
  purpose:
    'A goal learners can pick. Their goals choose the topics suggested on the next screens.',
});

const goalTag = (what: string): Control => ({
  name: `Goal tag: ${what}`,
  purpose:
    'A short tag on each topic this goal suggests, so learners see why a topic fits.',
  shownWhen: 'On Your starting topic, when the learner picked this goal.',
});

const workAnswer = (what: string): Control => ({
  name: `Role answer: ${what}`,
  purpose: 'A role learners can pick. Saved to their profile.',
});

const familiarityAnswer = (what: string): Control => ({
  name: `Familiarity answer: ${what}`,
  purpose:
    'How well the learner knows their starting topic. Picking it moves on at once.',
  goesTo: NEXT,
});

const reminderSlot = (what: string, time: string): Control => ({
  name: `Reminder time: ${what}`,
  purpose: `Picks a daily reminder at ${time}, shown under it as the phone writes times.`,
});

const pathTitle = (what: string): Control => ({
  name: `Path name: ${what}`,
  purpose:
    'The name of the learner’s path, above the next lesson at the top of Home. Their first goal chooses it.',
  shownWhen: `On Home after onboarding, when the learner’s first goal is ${what}.`,
});

const SETTINGS_ONLY =
  'Only in Settings → Learning reminder, which reopens this screen. Never during onboarding.';

export const CONTROLS: Record<string, Control> = {
  'welcome.title': {
    name: 'Headline',
    purpose: 'The first words a new learner reads. Says what Decide is.',
  },
  'welcome.body': {
    name: 'Subheadline',
    purpose: 'One line under the headline on how Decide teaches.',
  },
  'welcome.start': {
    name: 'Start button',
    purpose: 'Starts onboarding.',
    goesTo: `${NEXT}, Light or dark by default`,
  },
  'welcome.signIn': {
    name: 'Existing-account button',
    purpose:
      'For learners who already have an account. In the preview it starts over, because the preview has no accounts.',
    goesTo: 'Sign-in',
  },

  'theme.title': {
    name: 'Question',
    purpose: 'What the screen asks. The Light and Dark card labels are fixed.',
  },
  'theme.body': {
    name: 'Subheadline',
    purpose: 'One line under the question, saying the theme can change later.',
  },
  'theme.go': {
    name: 'Continue button',
    purpose: 'Saves the picked theme on the device and moves on.',
    goesTo: `${NEXT}, Meet LEO by default`,
  },

  'meetLeo.hello': {
    name: 'Headline',
    purpose: 'LEO’s greeting, beside LEO.',
  },
  'meetLeo.body': {
    name: 'LEO’s message',
    purpose: 'Tells learners what happens next: a few questions, then a case.',
  },
  'meetLeo.go': {
    name: 'Next button',
    purpose: 'Moves on to the questions.',
    goesTo: `${NEXT}, Your learning goal by default`,
  },

  'goal.title': { name: 'Question', purpose: 'What the screen asks.' },
  'goal.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  ...perGoal('goal', goalAnswer),
  'goal.SOMETHING_ELSE': {
    name: 'Goal answer: something else',
    purpose:
      'Counts as one pick and opens a field for the learner’s own goal. Continue stays off until they write it. Their words never choose topics.',
  },
  'goal.explore': {
    name: '“Just exploring” link',
    purpose: 'Clears any picked goals and skips the question.',
    goesTo: NEXT,
  },
  'goal.fieldLabel': {
    name: 'Own-goal field label',
    purpose: 'The name above the field for the learner’s own goal.',
    shownWhen: 'After the learner picks “Something else”.',
  },
  'goal.fieldPlaceholder': {
    name: 'Own-goal field example',
    purpose: 'Example text inside the field until the learner types.',
    shownWhen: 'After the learner picks “Something else”.',
  },
  'goal.fieldHelper': {
    name: 'Own-goal field hint',
    purpose: 'A short hint under the field.',
    shownWhen: 'After the learner picks “Something else”.',
  },
  'goal.limit_one': {
    name: 'Pick limit note (limit 1)',
    purpose: 'Tells learners how many goals they can pick.',
    shownWhen: 'When the limit below is 1.',
  },
  'goal.limit_other': {
    name: 'Pick limit note (limit 2 or 3)',
    purpose: 'Tells learners how many goals they can pick.',
    shownWhen: 'When the limit below is 2 or 3.',
    values: { count: 'The limit set below.' },
  },
  ...perGoal('goal.tag', goalTag),

  'interests.title': { name: 'Question', purpose: 'What the screen asks.' },
  'interests.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  'interests.search': {
    name: 'Search field placeholder',
    purpose:
      'Text inside the search box until the learner types. Screen readers also read it as the box’s name.',
  },
  'interests.noMatch': {
    name: 'No-results message',
    purpose: 'Says the search found nothing.',
    shownWhen: 'When a search matches no topic.',
    values: { query: 'What the learner typed in the search box.' },
  },
  'interests.noneSelected': {
    name: 'Picked count (none yet)',
    purpose: 'Above Continue. Continue stays off until a topic is picked.',
    shownWhen: 'Before any topic is picked.',
  },
  'interests.selected_one': {
    name: 'Picked count (1 topic)',
    purpose: 'Above Continue: how many topics are picked.',
    shownWhen: 'When exactly 1 topic is picked.',
    values: { count: 'How many topics the learner picked.' },
  },
  'interests.selected_other': {
    name: 'Picked count (2 or more)',
    purpose: 'Above Continue: how many topics are picked.',
    shownWhen: 'When 2 or more topics are picked.',
    values: { count: 'How many topics the learner picked.' },
  },
  'interests.viewSelected': {
    name: 'Show-picks link',
    purpose: 'Opens a list of the picked topics, each with a remove button.',
    shownWhen: 'Once a topic is picked, while the list is closed.',
  },
  'interests.hideSelected': {
    name: 'Hide-picks link',
    purpose: 'Closes the list of picked topics.',
    shownWhen: 'While the list of picked topics is open.',
  },
  'interests.remove': {
    name: 'Remove button (read aloud)',
    purpose:
      'Screen readers read it for the × beside each picked topic. Pressing it removes that topic.',
    shownWhen: 'Never as text; only read aloud, in the list of picks.',
    values: { topic: 'The topic that row removes.' },
  },
  'interests.help': {
    name: '“Help me choose” link',
    purpose: 'Opens a box of topics suggested for the learner’s goals.',
    shownWhen:
      'When the learner’s goals suggest topics, while the box is closed.',
  },
  'interests.helpTitle': {
    name: 'Suggestions box heading',
    purpose: 'The heading of the box of suggested topics.',
    shownWhen: 'While the suggestions box is open.',
  },
  'interests.useThese': {
    name: 'Add-suggestions button',
    purpose:
      'Adds every suggested topic to the picks and closes the box. The learner stays here and can still change them.',
    shownWhen: 'While the suggestions box is open.',
  },
  'interests.chooseOwn': {
    name: 'Close-suggestions link',
    purpose: 'Closes the box without adding anything.',
    shownWhen: 'While the suggestions box is open.',
  },

  'focus.title': { name: 'Question', purpose: 'What the screen asks.' },
  'focus.body': {
    name: 'Subheadline',
    purpose: 'One line under the question, on how the other interests follow.',
  },
  'focus.suggested': {
    name: 'Suggested label',
    purpose:
      'Marks the topic the learner’s goals suggest. That topic is listed first.',
    shownWhen: 'When at least one interest is ready.',
  },
  'focus.start': {
    name: 'Start button',
    purpose:
      'Starts with the topic the learner picked. Off until they pick one.',
    goesTo: NEXT,
    shownWhen: 'When at least one interest is ready.',
  },
  'focus.noneReady': {
    name: 'Nothing-ready message',
    purpose: 'Says none of the learner’s topics can start yet.',
    shownWhen: 'When none of the learner’s interests is ready.',
  },
  'focus.tryCase': {
    name: 'Try-another-topic button',
    purpose:
      'Starts with the ready topic that best fits the learner’s goals, outside their interests.',
    goesTo: NEXT,
    shownWhen: 'When none of the interests is ready, but another topic is.',
    values: {
      topic: 'The ready topic that best fits the learner’s goals.',
    },
  },
  'focus.editInterests': {
    name: 'Edit-interests button',
    purpose: 'Lets the learner pick different topics.',
    goesTo: 'Choose your interests',
    shownWhen: 'When none of the learner’s interests is ready.',
  },
  'work.title': { name: 'Question', purpose: 'What the screen asks.' },
  'work.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  'work.STUDENT': workAnswer('student'),
  'work.EARLY_CAREER': workAnswer('early career'),
  'work.PROFESSIONAL': workAnswer('professional'),
  'work.MANAGER': workAnswer('manager'),
  'work.DEPARTMENT_HEAD': workAnswer('department head'),
  'work.EXECUTIVE': workAnswer('executive'),
  'work.FOUNDER': workAnswer('founder'),
  'work.FREELANCER': workAnswer('freelancer'),
  'work.BETWEEN_ROLES': workAnswer('between roles'),
  'work.SOMETHING_ELSE': {
    name: 'Role answer: something else',
    purpose:
      'A role learners can pick. Also opens the job-title field, which stays optional.',
  },
  'work.addTitle': {
    name: 'Add-job-title link',
    purpose: 'Opens the optional job-title field.',
    shownWhen: 'Until the field is open.',
  },
  'work.fieldLabel': {
    name: 'Job-title field label',
    purpose: 'The name above the optional job-title field.',
    shownWhen:
      'After the learner presses the add-job-title link or picks “Something else”.',
  },
  'work.fieldPlaceholder': {
    name: 'Job-title field example',
    purpose: 'Example text inside the field until the learner types.',
    shownWhen:
      'After the learner presses the add-job-title link or picks “Something else”.',
  },
  'work.skip': {
    name: 'Skip link',
    purpose:
      'Moves on without a role. Continue stays off until a role is picked.',
    goesTo: NEXT,
  },

  'familiarity.title': {
    name: 'Question',
    purpose: 'What the screen asks, about the learner’s starting topic.',
    values: { topic: 'The learner’s starting topic.' },
  },
  'familiarity.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  'familiarity.NEW': familiarityAnswer('new to it'),
  'familiarity.BASICS': familiarityAnswer('knows the basics'),
  'familiarity.SOMETIMES': familiarityAnswer('uses it sometimes'),
  'familiarity.OFTEN': familiarityAnswer('uses it often'),

  'pace.title': { name: 'Question', purpose: 'What the screen asks.' },
  'pace.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  'pace.minutes': {
    name: 'Pace answer',
    purpose:
      'Each of the three answers: 1, 2 or 3 lessons a day, shown as the minutes they take.',
    values: { count: 'The minutes a day: 5, 10 or 15.' },
  },
  'pace.detail1': {
    name: 'Pace name: 5 minutes',
    purpose: 'The word under the 5-minute answer.',
  },
  'pace.detail2': {
    name: 'Pace name: 10 minutes',
    purpose: 'The word under the 10-minute answer.',
  },
  'pace.detail3': {
    name: 'Pace name: 15 minutes',
    purpose: 'The word under the 15-minute answer.',
  },
  'pace.go': {
    name: 'Next button',
    purpose: 'Saves the pace and moves on.',
    goesTo: NEXT,
  },

  'streakIntro.title': {
    name: 'Headline',
    purpose: 'Introduces the streak goal asked next.',
  },
  'streakIntro.body': {
    name: 'Subheadline',
    purpose: 'One line on why a goal helps and what it pays.',
  },
  'streakIntro.next': {
    name: 'Next button',
    purpose: 'Moves on to the goal question.',
    goesTo: NEXT,
  },

  'streakGoal.title': { name: 'Question', purpose: 'What the screen asks.' },
  'streakGoal.option': {
    name: 'Goal answer',
    purpose: 'Each of the four goals, with the bonus XP reaching it pays.',
    values: {
      count: 'The goal in days: 7, 14, 30 or 50.',
      xp: 'The bonus XP that goal pays: 100, 250, 600 or 1,000.',
    },
  },
  'streakGoal.helper': {
    name: 'Day rule',
    purpose: 'The line under the goals: what counts as a day.',
  },
  'streakGoal.commit': {
    name: 'Commit button',
    purpose:
      'Saves the goal and moves on. Off until a goal is picked; the account gets the goal at sign-up.',
    goesTo: NEXT,
  },

  'preparing.title': {
    name: 'Headline',
    purpose: 'Says the path is being put together.',
    shownWhen: 'For the moment the path takes to build, before Your path.',
  },
  'preparing.starting': {
    name: 'Starting-topic line',
    purpose: 'Names the topic the path starts with.',
    shownWhen: 'While the path builds, when there is a starting topic.',
    values: { topic: 'The learner’s starting topic.' },
  },
  'preparing.interests': {
    name: 'Interests line',
    purpose: 'Says the path also brings in the other picked topics.',
    shownWhen:
      'While the path builds, when the learner picked more than one topic.',
  },
  'preparing.lessons': {
    name: 'Lessons line',
    purpose: 'Says the next lessons are being chosen.',
    shownWhen: 'While the path builds.',
  },
  'path.title': {
    name: 'Headline',
    purpose: 'The first words on the learner’s path.',
  },
  'path.body': {
    name: 'Subheadline (several topics)',
    purpose:
      'Says the first challenge comes first, then the starting topic and the rest.',
    shownWhen: 'When the learner picked more than one topic.',
    values: { topic: 'The learner’s starting topic.' },
  },
  'path.bodyOne': {
    name: 'Subheadline (one topic)',
    purpose: 'Says the first challenge comes first, then the starting topic.',
    shownWhen: 'When the learner picked one topic.',
    values: { topic: 'The learner’s starting topic.' },
  },
  'path.bodyNone': {
    name: 'Subheadline (no topic ready)',
    purpose:
      'Says the first challenge comes first, and the topics’ lessons later.',
    shownWhen: 'When none of the learner’s topics has lessons yet.',
  },
  'path.challenge': {
    name: 'Challenge card label',
    purpose: 'The small label on the first challenge’s card, above its title.',
  },
  'path.about': {
    name: 'Challenge card description',
    purpose: 'What the first challenge covers. The same for every learner.',
  },
  'path.duration': {
    name: 'Challenge card length',
    purpose: 'How long the first challenge takes.',
  },
  'path.start': {
    name: 'Start button',
    purpose: 'Opens the first challenge.',
    goesTo: 'Your first challenge',
  },
  'path.comingUp': {
    name: '“Then” label',
    purpose:
      'The small label above the starting topic, after the challenge card.',
    shownWhen: 'When there is a starting topic.',
  },
  'path.firstLesson': {
    name: 'First-lesson line',
    purpose: 'Names the starting topic’s first lesson.',
    shownWhen: 'When the starting topic has a first lesson.',
    values: { title: 'The title of the starting topic’s first lesson.' },
  },
  ...perGoal('pathTitle', pathTitle),
  'pathTitle.EXPLORING': {
    ...pathTitle('just exploring'),
    shownWhen: 'On Home after onboarding, when the learner picked no goal.',
  },

  'challenge.eyebrow': {
    name: 'Question label (first)',
    purpose: 'The small label above the challenge’s questions.',
    shownWhen: 'Up to and on the first question.',
  },
  'challenge.again': {
    name: 'Question label (later)',
    purpose: 'The small label above each question after the first.',
    shownWhen: 'From the second question on.',
  },
  'challenge.check': {
    name: 'Check button',
    purpose:
      'Sends the learner’s answer and shows whether it was the strongest call.',
  },
  'challenge.strong': {
    name: 'Strong-answer heading',
    purpose: 'Heads the feedback after an answer.',
    shownWhen: 'After the learner makes the strongest call.',
  },
  'challenge.consider': {
    name: 'Other-answer heading',
    purpose: 'Heads the feedback after an answer.',
    shownWhen: 'After the learner makes any other call.',
  },
  'challenge.next': {
    name: 'Next-question button',
    purpose: 'Moves on to the next question.',
    shownWhen: 'After the feedback, on every question but the last.',
  },
  'challenge.progress': {
    name: 'Finish button',
    purpose: 'Ends the challenge.',
    goesTo: NEXT,
    shownWhen: 'After the feedback on the last question.',
  },

  'complete.title': {
    name: 'Headline',
    purpose: 'Says the first challenge is done.',
  },
  'complete.body': {
    name: 'Subheadline (no strengths)',
    purpose: 'One line under the headline.',
    shownWhen: 'When the learner made the strongest call in no topic.',
  },
  'complete.strengthsOne': {
    name: 'Subheadline (1 strength)',
    purpose: 'Names the topic where the learner made the strongest call.',
    shownWhen: 'When there is 1 such topic.',
    values: { a: 'That topic.' },
  },
  'complete.strengthsTwo': {
    name: 'Subheadline (2 strengths)',
    purpose: 'Names the topics where the learner made the strongest call.',
    shownWhen: 'When there are 2 such topics.',
    values: { a: 'The first topic.', b: 'The second topic.' },
  },
  'complete.strengthsThree': {
    name: 'Subheadline (3 strengths)',
    purpose: 'Names the topics where the learner made the strongest call.',
    shownWhen: 'When there are 3 or more such topics; the first 3 are named.',
    values: {
      a: 'The first topic.',
      b: 'The second topic.',
      c: 'The third topic.',
    },
  },
  'complete.done': {
    name: 'Progress card heading',
    purpose: 'Heads the card counting the finished challenge.',
  },
  'complete.takeaway': {
    name: 'Takeaway line',
    purpose: 'The challenge’s one takeaway, on the progress card.',
    values: { takeaway: 'The first challenge’s takeaway.' },
  },
  'complete.next': {
    name: 'Next-lesson line',
    purpose: 'Names the lesson that comes next, on the progress card.',
    shownWhen: 'When the starting topic has a first lesson.',
    values: { title: 'The title of the starting topic’s first lesson.' },
  },
  'complete.save': {
    name: 'Save-takeaway button',
    purpose: 'Would save the takeaway for later.',
    shownWhen: 'Never yet: the app cannot save takeaways.',
  },

  'reminder.title': { name: 'Question', purpose: 'What the screen asks.' },
  'reminder.body': {
    name: 'Subheadline',
    purpose: 'One line under the question.',
  },
  'reminder.MORNING': reminderSlot('morning', '8:00'),
  'reminder.LUNCH': reminderSlot('lunch', '12:30'),
  'reminder.EVENING': reminderSlot('evening', '19:00'),
  'reminder.CUSTOM': {
    name: 'Reminder time: own time',
    purpose:
      'Opens a time picker for the learner’s own time. On iPhone it is also the picker’s title.',
  },
  'reminder.pick': {
    name: 'Own-time placeholder',
    purpose: 'Stands under the own-time answer where its time will show.',
    shownWhen: 'Until the learner picks their own time.',
  },
  'reminder.go': {
    name: 'Continue button',
    purpose:
      'Saves the reminder and asks the phone for permission to notify. A refusal still saves the time.',
    goesTo: NEXT,
    shownWhen: 'Always; it can be pressed once a time is picked.',
  },
  'reminder.skip': {
    name: '“No reminders” link',
    purpose: 'Moves on with no reminder.',
    goesTo: NEXT,
  },
  'reminder.editBody': {
    name: 'Subheadline (Settings)',
    purpose: 'One line under the heading, on how the daily reminder works.',
    shownWhen: SETTINGS_ONLY,
  },
  'reminder.off': {
    name: 'Turn-off button (Settings)',
    purpose: 'Turns the learner’s reminder off.',
    shownWhen: SETTINGS_ONLY,
  },
  'reminder.blocked': {
    name: 'Notifications-off message (Settings)',
    purpose:
      'Says the phone blocks this app’s notifications, so the reminder cannot arrive.',
    shownWhen: `${SETTINGS_ONLY} Only while notifications are off.`,
  },
  'reminder.allow': {
    name: 'Turn-on-notifications button (Settings)',
    purpose: 'Asks for notifications again, or opens the phone’s settings.',
    shownWhen: `${SETTINGS_ONLY} Only while notifications are off.`,
  },

  'account.title': {
    name: 'Headline',
    purpose: 'Why to create an account. An account is required here.',
  },
  'account.body': {
    name: 'Subheadline',
    purpose: 'One line under the headline.',
  },
  'account.email': {
    name: 'Email button',
    purpose:
      'Creates an account with an email address. Listed last, under the other sign-up buttons.',
    goesTo: 'Sign-up with email',
  },
  'account.ask': {
    name: 'Existing-account question',
    purpose: 'Asked before the sign-in link, at the bottom.',
  },
  'account.signIn': {
    name: 'Sign-in link',
    purpose: 'For learners who already have an account.',
    goesTo: 'Sign-in',
  },
  'account.agree': {
    name: 'Terms sentence',
    purpose: 'The start of the sentence at the bottom, before the Terms link.',
  },
  'account.terms': {
    name: 'Terms link',
    purpose: 'Opens the terms of use in the browser.',
    goesTo: 'Terms page',
  },
  'account.and': {
    name: 'Terms sentence: “and”',
    purpose: 'The word between the two links.',
  },
  'account.privacy': {
    name: 'Privacy link',
    purpose: 'Opens the privacy policy in the browser.',
    goesTo: 'Privacy page',
  },
};

/** Readable names for the last segment of a key the table does not cover yet. */
const LEAF: Record<string, string> = {
  title: 'Headline',
  hello: 'Headline',
  body: 'Subheadline',
  start: 'Primary button text',
  go: 'Primary button text',
  next: 'Primary button text',
  signIn: 'Secondary button text',
  skip: 'Skip button text',
  explore: '“Just exploring” link',
  fieldLabel: 'Field label',
  fieldPlaceholder: 'Field placeholder',
  fieldHelper: 'Helper text',
};

const humanize = (leaf: string): string => {
  const words = leaf.replace(/([a-z])([A-Z0-9])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The console's label for a copy key: what the text is, not where it is stored. */
export function labelOf(key: string): string {
  const control = CONTROLS[key];
  if (control !== undefined) return control.name;
  const leaf = key.split('.').at(-1) ?? key;
  if (LEAF[leaf] !== undefined) return LEAF[leaf];
  if (/^[A-Z_]+$/.test(leaf))
    return `Answer: ${humanize(leaf.toLowerCase().replace(/_/g, ' '))}`;
  return humanize(leaf);
}
